import { Button } from '@shared/components/ui/button';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { faRedo, faTimes } from '@fortawesome/pro-light-svg-icons';
import { memo, useCallback, useRef } from 'react';

interface ImportHistorySelectionToolbarProps {
  selectedCount: number;
  onClose: () => void;
  onRetrySelected: () => void;
  retryableCount: number;
}

export const ImportHistorySelectionToolbar = memo(
  ({ selectedCount, onClose, onRetrySelected, retryableCount }: ImportHistorySelectionToolbarProps) => {
    const { t } = useTranslation('shopsModule');
    const confirmModalRef = useRef<ConfirmModalRef>(null);

    const handleRetry = useCallback(() => {
      if (retryableCount > 0) {
        confirmModalRef.current?.open(
          () => onRetrySelected(),
          undefined,
          t('importHistory.confirmModals.bulkRetry.title', 'Reintentar importaciones'),
          t('importHistory.confirmModals.bulkRetry.message', {
            count: retryableCount,
            defaultValue: `¿Estás seguro de que quieres reintentar las ${retryableCount} importaciones fallidas seleccionadas?`,
          }),
          t('common.confirm', 'Confirmar'),
          t('common.cancel', 'Cancelar'),
        );
      }
    }, [onRetrySelected, retryableCount, t]);

    if (selectedCount === 0) {
      return null;
    }

    return (
      <>
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-card-bg border border-card-border rounded-lg p-2 shadow-lg">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 bg-primary text-white text-sm rounded-full flex items-center justify-center">
                  {selectedCount}
                </span>
                <span className="text-sm font-medium">{t('importHistory.selection.selected', 'seleccionados')}</span>
              </div>

              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRetry}
                  disabled={retryableCount === 0}
                  className="border-card-border bg-transparent"
                >
                  <FontAwesomeIcon icon={faRedo} className="w-3 h-3 mr-1 text-primary" />
                  {t('importHistory.actions.retry', 'Reintentar')} ({retryableCount})
                </Button>
              </div>

              <Button variant="ghost" size="sm" onClick={onClose} className="p-0 hover:bg-transparent">
                <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
        <ConfirmModal ref={confirmModalRef} />
      </>
    );
  },
);

ImportHistorySelectionToolbar.displayName = 'ImportHistorySelectionToolbar';
